import React from 'react';

import {useMobileMenu} from './state';

const {RawHTML} = polyphony.components.generic;
const {combineRefs} = polyphony.util;
const {scrolling} = polyphony.dom;
const {withExplicitProps} = polyphony.hoc;
const {prefixCSS} = polyphony.css;
const {useHotkeys, useA11y, useRefWithListeners} = polyphony.hooks;

const styles = {
    text: prefixCSS({
        color: 'inherit',
        transition: 'color .5s ease-in-out'
    }),
    disclosureSVG: prefixCSS({
        transition: 'color .5s ease-in-out',
        color: 'inherit',
        height: 24,
        width: 24
    }),
    opener: prefixCSS({
        width: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'inherit',
        transition: 'color .5s ease-in-out',
        boxSizing: 'border-box'
    }),
    link: prefixCSS({
        padding: '5px 10px',
        color: 'inherit',
        boxSizing: 'border-box',
        height: 54,
        maxWidth: "100%",
        width: '100%',
        justifyContent: "flex-start",
        alignItems: "center",
        display: "flex"
    })
};

const svgs = {
    disclosure: <svg aria-hidden='true' style={styles.disclosureSVG} viewBox="0 0 256 512"><path fill="currentColor" d="M17.525 36.465l-7.071 7.07c-4.686 4.686-4.686 12.284 0 16.971L205.947 256 10.454 451.494c-4.686 4.686-4.686 12.284 0 16.971l7.071 7.07c4.686 4.686 12.284 4.686 16.97 0l211.051-211.05c4.686-4.686 4.686-12.284 0-16.971L34.495 36.465c-4.686-4.687-12.284-4.687-16.97 0z" /></svg>
};

const MobileMenuItem = withExplicitProps(({item, depth = 0, number, setsize}) => {
    const {isOpen,
           expand, retract, expandedIds} = useMobileMenu();
    const {id, parent_id, children, title, url} = item;

    const isExpanded = expandedIds.includes(id);

    const hkref = useHotkeys({
        Space: e => e.target.click(),
        Enter: e => e.target.click(),
        ArrowDown: e => {
            e.preventDefault();
            polyphony.setFocusMode('tab');
            let el = e.target;
            let baseLevel = el.getAttribute('aria-level');

            while (el.nextSibling && el.nextSibling.getAttribute('aria-level') !== baseLevel) {
                el = el.nextSibling;
            }

            if (el.nextSibling && el.nextSibling.getAttribute('aria-level') === baseLevel) {
                el.nextSibling.focus();
            }
        },
        ArrowUp: e => {
            e.preventDefault();
            polyphony.setFocusMode('tab');
            let el = e.target;
            let baseLevel = el.getAttribute('aria-level');

            while (el.previousSibling && el.previousSibling.getAttribute('aria-level') !== baseLevel) {
                el = el.previousSibling;
            }

            if (el.previousSibling && el.previousSibling.getAttribute('aria-level') === baseLevel) {
                el.previousSibling.focus();
            }
        },
        ArrowRight: e => {
            e.preventDefault();
            polyphony.setFocusMode('tab');
            if (isExpanded) {
                if (children && children.length > 0) {
                    e.target.nextSibling.focus();
                }
            } else {
                expand(id);
            }
        },
        ArrowLeft: e => {
            e.preventDefault();
            polyphony.setFocusMode('tab');
            if (isExpanded) {
                retract(id);
            } else {
                let el = e.target;
                while (parseInt(el.getAttribute('aria-level')) === depth + 1) {
                    el = el.previousSibling;
                }
                el.focus();
            }
        }/*,
        Home: e => {}, // Go to first open node
        End: e => {}, // Go to last open node
        '*': e => {} // Expand all nodes at focused level
        */
        // TODO: a-zA-Z - Move to the nearest node that starts with the letter pressed
    }, [isExpanded, id, parent_id, children && children.length > 0 ? children[0].id : false]);

    const [{/*focus*/}, bref] = useA11y('treeitem', {}, isOpen);

    const ref = combineRefs([hkref, bref]);

    const lref = useRefWithListeners({
        click: e => {
            e.stopImmediatePropagation();
            e.preventDefault();
            if (isExpanded) retract(id);
            else            expand(id);
        }
    }, [isExpanded, id, expand, retract]);

    return (
        <>
            <a role='treeitem'
               key={id}
               tabIndex={isOpen ? '0' : '-1'}
               aria-expanded={isExpanded && children ? 'true' : 'false'}
               aria-posinset={number}
               aria-level={depth + 1}
               aria-setsize={setsize}
               aria-label={title}
               href={url}
               ref={ref}
               onClick={() => scrolling.enable()}
               style={{...styles.link,
                       paddingLeft: (children && children.length > 0 ? 5 : 44) + depth * 44,
                       color: isExpanded ? 'gray' : isOpen ? 'black' : 'transparent'}}>
                {children && children.length > 0 &&
                <div role="button"
                     aria-pressed={isExpanded ? 'true' : 'false'}
                     aria-label={`${isExpanded ? 'Hide' : 'Show'} Children of "${title}"`}
                     ref={lref}
                     style={{...styles.opener, transform: isExpanded ? 'rotate(90deg)' : 'none'}}>
                    {svgs.disclosure}
                </div>}
                <span role='text' style={styles.text}><RawHTML>{title}</RawHTML></span>
            </a>
            {children && isExpanded && children.map((c, i) => <MobileMenuItem key={c.id} item={c} depth={depth + 1} number={i} setsize={children.length} />)}
        </>
    );
}, ['item', 'depth', 'number']);

MobileMenuItem.displayName = 'MobileMenuItem';

export default MobileMenuItem;

